/**
 * Seasonal Context Section
 * Provides seasonal awareness and encouraging context
 * Only shown when "Everything" is selected (isOverwhelmed = true)
 * UX Philosophy: Emotionally Supportive, Strategically Transparent
 */

import React from 'react';
import { Calendar, TrendingUp } from 'lucide-react';

const SeasonalContextSection = ({ currentDate }) => {
  // Get current month for seasonal messaging
  const month = currentDate.getMonth(); // 0-11

  // Seasonal insights based on college application timeline
  const getSeasonalInsight = () => {
    if (month >= 8 && month <= 9) {
      // September-October: ED/EA prep
      return {
        phase: 'Early Decision/Action Prep',
        message: "This is typically an intense period as ED/EA deadlines approach. Remember that it's normal to feel pressure right now - you're in the peak intensity phase.",
        tip: 'Focus on completing must-do tasks first. Many others can be simplified or deferred to after early deadlines.',
        color: 'amber'
      };
    } else if (month === 10) {
      // November: ED/EA submission + RD prep
      return {
        phase: 'Application Submission Season',
        message: "November is peak intensity for college applications. What you're feeling is completely normal - this is the most demanding time of the entire process.",
        tip: 'Prioritize ruthlessly. Some RD tasks can wait until after early deadlines pass.',
        color: 'red'
      };
    } else if (month === 11) {
      // December: RD applications
      return {
        phase: 'Regular Decision Season',
        message: "You're in the RD application window. The pressure is real, but there's more flexibility now than during ED/EA season.",
        tip: 'Quality matters for RD applications. Take the time to do them well.',
        color: 'indigo'
      };
    } else if (month >= 0 && month <= 1) {
      // January-February: RD deadlines + waiting
      return {
        phase: 'Final Submissions',
        message: "Final RD deadlines are approaching. Once these are submitted, you'll have breathing room while you wait for decisions.",
        tip: 'Stay focused on the finish line. You\'re almost through the most intense part.',
        color: 'purple'
      };
    } else if (month >= 2 && month <= 4) {
      // March-May: Decisions + enrollment
      return {
        phase: 'Decision Season',
        message: "Decisions are rolling in! This is an exciting but sometimes stressful time as you weigh your options.",
        tip: 'Focus on comparing offers and visiting schools if possible. Financial aid tasks become priority now.',
        color: 'teal'
      };
    } else {
      // Summer: Planning + preparation
      return {
        phase: 'Planning & Preparation',
        message: "Summer is the perfect time to get ahead. Building a strong foundation now will reduce stress in the fall.",
        tip: 'Focus on essays, research, and profile building. You have time to do quality work.',
        color: 'emerald'
      };
    }
  };

  const insight = getSeasonalInsight();
  const colorClasses = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    teal: 'bg-teal-50 border-teal-200 text-teal-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900'
  };

  return (
    <div className={`rounded-lg p-4 border ${colorClasses[insight.color]}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Calendar className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-2">
          <h4 className="text-sm font-bold">{insight.phase}</h4>
          <p className="text-xs leading-relaxed">{insight.message}</p>
          <div className="flex items-start gap-2 pt-2 border-t border-current/20">
            <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs leading-relaxed">
              <strong>Tip:</strong> {insight.tip}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalContextSection;
